import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { MDBBadge, MDBCol, MDBIcon } from "mdbreact";
import { SecondaryFooter, PrimaryFooter } from "./footer";

import Tagging from "./body/tagging";
import { capitalize, fullName } from "../../../../../../services/utilities";
import "./style.css";

const getDay = (date) =>
  new Date(date).toLocaleDateString("en-US", { weekday: "short" });

// shows the weekday only when it is not today
const getTime = (date) => {
  if (!date) return "";
  const day = getDay(date) === getDay(new Date()) ? "" : `${getDay(date)} `;
  return `${day}${new Date(date).toLocaleTimeString()}`;
};

// 09171234567 -> 0917-123-4567
const formatMobile = (mobile = "") => {
  const digits = String(mobile || "").replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("0"))
    return `${digits.slice(0, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  if (digits.length === 10 && digits.startsWith("9"))
    return `0${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return mobile || "";
};

export default function Card({ item, index }) {
  const { activePlatform } = useSelector(({ auth }) => auth),
    { collections: sources } = useSelector(({ providers }) => providers),
    [deal, setDeal] = useState({}),
    [edit, setEdit] = useState(false);
  const {
      customerId = {},
      createdAt,
      rendered = [],
      physicianId = {},
      source: forwardedBy = {},
      _id,
      cart = [],
    } = deal,
    { fullName: fullname = {}, mobile = "", isMale = false } = customerId || {},
    source = sources.find(({ vendors }) => vendors?._id === forwardedBy?._id),
    { at, dept } =
      rendered.find(
        ({ dept }) =>
          dept === (activePlatform.department === "Laboratory" ? "LAB" : "RAD")
      ) || {};

  useEffect(() => {
    setDeal(item);
  }, [item]);

  // deal.source is the referring branch, populated with name/displayname
  const sourceName =
    forwardedBy?.displayname || forwardedBy?.name || source?.name || "Walk in";
  const referral = fullName(physicianId?.fullName);
  // LNAME, Fname Mname in one line
  const patientName = `${String(fullname.lname || "").toUpperCase()}, ${capitalize(
    fullname.fname
  )}${fullname.mname ? ` ${capitalize(fullname.mname)}` : ""}`;

  return (
    <MDBCol className="p-2" md="6" lg="4" key={index}>
      <div className={`onb-card ${dept ? "rendered" : ""}`}>
        <div className="onb-head">
          <span
            className={`onb-avatar ${isMale ? "" : "female"}`}
            title={`Patient No. ${deal?.pn ?? ""} (${isMale ? "Male" : "Female"})`}
          >
            {deal?.pn}
          </span>
          <div className="onb-who">
            <div className="onb-name onb-ellipsis" title={patientName}>
              {patientName}
            </div>
            <div className="onb-row onb-muted">
              <MDBIcon icon="phone-alt" className="text-danger" />
              <span className="onb-ellipsis">
                {formatMobile(mobile) || <i>No mobile</i>}
              </span>
            </div>
          </div>
          {cart?.length > 0 && (
            <div className="onb-tests" title="Availed">
              {cart.map(({ abbreviation, description, referenceId }) => (
                <MDBBadge key={referenceId} color="primary" title={description}>
                  {abbreviation || description}
                </MDBBadge>
              ))}
            </div>
          )}
        </div>

        {edit ? (
          <>
            <div className="onb-tagging">
              <Tagging
                forwardedBy={forwardedBy}
                physicianId={physicianId}
                _id={_id}
              />
            </div>
            <SecondaryFooter setEdit={setEdit} />
          </>
        ) : (
          <>
            <div className="onb-row onb-meta">
              <MDBIcon icon="hospital-alt" className="text-primary" />
              <span className="onb-ellipsis" title={`Source: ${sourceName}`}>
                {capitalize(sourceName)}{" "}
                <span className="onb-muted">@ {getTime(createdAt)}</span>
              </span>
              <span
                className={`onb-status ${dept ? "done" : ""}`}
                title={dept ? `Rendered ${getTime(at)}` : "Not yet rendered"}
              >
                <MDBIcon icon={dept ? "check-circle" : "hourglass-half"} />
                {dept ? getTime(at) : "Pending"}
              </span>
            </div>

            <div className="onb-content">
              <div className="onb-row onb-details" title={`Referral: ${referral}`}>
                <MDBIcon icon="user-md" className="text-info" />
                <span className="onb-ellipsis">
                  {referral !== "-" ? referral : (
                    <i className="onb-muted">No referral</i>
                  )}
                </span>
                <MDBIcon
                  icon="pen"
                  size="xs"
                  className="onb-edit"
                  title="Edit source and referral"
                  onClick={() => setEdit(true)}
                />
              </div>
              <PrimaryFooter deal={{ ...deal, patientNo: index + 1 }} />
            </div>
          </>
        )}
      </div>
    </MDBCol>
  );
}
